import { useState } from 'react';
import { ChevronDown, ChevronRight, Play, TestTube } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useWorkflowRuns, useWorkflowRun } from '@/hooks/useWorkflows';
import type { ApiWorkflowRun, ApiWorkflowStepLog } from '@/api/workflows';

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function StepLogRow({ step }: { step: ApiWorkflowStepLog }) {
  return (
    <div className="rounded border bg-muted/30 px-3 py-2 text-xs">
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className="text-xs">
          {step.status}
        </Badge>
        <span className="font-medium">{step.step_label}</span>
        {step.duration_ms != null && (
          <span className="text-muted-foreground">{step.duration_ms}ms</span>
        )}
      </div>
      {step.message && <p className="mt-1 text-muted-foreground">{step.message}</p>}
    </div>
  );
}

function RunRow({
  run,
  expanded,
  onToggle,
}: {
  run: ApiWorkflowRun;
  expanded: boolean;
  onToggle: () => void;
}) {
  const steps = run.step_logs ?? [];

  return (
    <div className="rounded-lg border bg-card">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-accent/50"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" />
        )}
        <span className="min-w-0 flex-1 truncate text-sm">{formatDate(run.started_at)}</span>
        {run.is_test_run ? (
          <TestTube className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <Play className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <Badge
          variant="secondary"
          className={cn(
            'shrink-0 text-xs',
            run.status === 'completed' && 'bg-green-500/15 text-green-700 dark:text-green-400',
            run.status === 'failed' && 'bg-destructive/15 text-destructive'
          )}
        >
          {run.status}
        </Badge>
      </button>
      {expanded && (
        <div className="border-t px-4 py-3">
          {run.summary && <p className="mb-2 text-sm text-muted-foreground">{run.summary}</p>}
          {run.error_message && (
            <p className="mb-2 text-sm text-destructive">{run.error_message}</p>
          )}
          <div className="space-y-2">
            {steps.length === 0 ? (
              <p className="text-xs text-muted-foreground">No step logs.</p>
            ) : (
              steps.map((step) => <StepLogRow key={step.id} step={step} />)
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export interface WorkflowRunHistoryProps {
  workflowId: string | null;
  open: boolean;
  onClose: () => void;
}

export function WorkflowRunHistory({ workflowId, open, onClose }: WorkflowRunHistoryProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { data, isLoading } = useWorkflowRuns(workflowId, { limit: 50 });
  const { data: expandedRun } = useWorkflowRun(expandedId);

  const runs = data?.data ?? [];
  const runWithSteps = expandedId && expandedRun ? expandedRun : null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl">
        <DialogHeader>
          <DialogTitle>Run history</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading runs…</p>
        ) : runs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No runs yet.</p>
        ) : (
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-2">
              {runs.map((run) => (
                <RunRow
                  key={run.id}
                  run={expandedId === run.id && runWithSteps ? runWithSteps : run}
                  expanded={expandedId === run.id}
                  onToggle={() => setExpandedId((id) => (id === run.id ? null : run.id))}
                />
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
